import { TranslateService } from '@ngx-translate/core';

import { printLiteral } from '@core/filters/path-filter/dsl/printer';
import { kpiTranslationKey } from '@core/filters/path-filter/kpi-names';
import {
  FieldType,
  FilterAssociation,
  FilterField,
  FilterVocabulary,
  SortableField,
  splitKeyedName
} from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FILTER_OP_SHAPES,
  FieldPath,
  FilterLiteral,
  FilterOpName,
  LabelLiteral,
  LabelLiterals,
  LabelRange,
  LabelText,
  LiteralRange,
  OpArgument,
  RelativeDateSpec,
  edgePath,
  fieldPath,
  segmentName
} from '@core/filters/path-filter/wire';
import { ICONS } from '@core/services/icons/icons.service';

import { SelectItem } from '@shared/components/select/select-item.model';
import { AvailableLanguages } from '@shared/types/translation.type';

import { DurationUnit, splitDuration } from './value/value-slot';

export const PATH_FILTER_I18N = 'features.data.path_filter';

function translated(translate: TranslateService, key: string): string | null {
  const text = translate.instant(key) as string;
  return text === key ? null : text;
}

function leaf(path: FieldPath): string {
  return segmentName(path.segments[path.segments.length - 1]);
}

function translatedKpi(translate: TranslateService, name: string): string | null {
  const key = kpiTranslationKey(name);
  return key === null ? null : translated(translate, key);
}

/**
 * The field's own title, else the table column translation of its leaf, else for a KPI the
 * translation the grids use, else the leaf.
 */
export function fieldLabel(
  field: Pick<FilterField, 'label' | 'path'>,
  lang: AvailableLanguages,
  translate: TranslateService
): string {
  const name = leaf(field.path);
  return (
    field.label?.[lang] ||
    translated(translate, `table.columns.${name}`) ||
    translatedKpi(translate, name) ||
    name
  );
}

/** The field of a condition at `path`, as a summary names it: a keyed field with its key. */
export function conditionFieldLabel(
  field: Pick<FilterField, 'label' | 'path' | 'keyed'>,
  path: FieldPath,
  lang: AvailableLanguages,
  translate: TranslateService
): string {
  const label = fieldLabel(field, lang, translate);
  const key = field.keyed ? splitKeyedName(leaf(path))?.key : undefined;
  return key ? `${label} [${key}]` : label;
}

export function associationLabel(
  association: Pick<FilterAssociation, 'edge' | 'label'>,
  lang: AvailableLanguages,
  translate: TranslateService
): string {
  return (
    association.label?.[lang] ||
    translated(translate, `table.columns.${association.edge}`) ||
    association.edge
  );
}

/** The title of the association at `edge` inside `scope`, the raw edge when the vocabulary does not know it. */
export function scopedAssociationLabel(
  vocabulary: FilterVocabulary,
  scope: string | null,
  edge: string,
  lang: AvailableLanguages,
  translate: TranslateService
): string {
  const association = vocabulary.association(edgePath(scope, edge));
  return association ? associationLabel(association, lang, translate) : edge;
}

/** A sort leaf behind an edge is named after its association too: `Company › Name`. */
export function sortableLabel(
  sortable: SortableField,
  vocabulary: FilterVocabulary,
  lang: AvailableLanguages,
  translate: TranslateService
): string {
  const own = fieldLabel(
    { label: sortable.label, path: fieldPath(sortable.field) },
    lang,
    translate
  );
  const edge = sortable.association?.edge;
  if (!edge) return own;
  const association = vocabulary.association(edge);
  return `${association ? associationLabel(association, lang, translate) : edge} › ${own}`;
}

export function operatorLabel(op: FilterOpName, translate: TranslateService): string {
  return translate.instant(`${PATH_FILTER_I18N}.ops.${op}`);
}

export function relativeDateLabel(
  spec: RelativeDateSpec,
  translate: TranslateService
): string {
  return typeof spec === 'string'
    ? translate.instant(`${PATH_FILTER_I18N}.relative_dates.${spec}`)
    : translate.instant(`${PATH_FILTER_I18N}.value.within_days`, { count: spec.within });
}

const UNIT_SECONDS: [DurationUnit, number][] = [
  ['days', 86400],
  ['hours', 3600],
  ['minutes', 60],
  ['seconds', 1]
];

/**
 * An ISO duration the way the duration input reads it, split over days, hours, minutes and
 * seconds so that no length is rounded; the raw text when no fixed length can be read from it.
 */
function durationText(iso: string, translate: TranslateService): string {
  const parsed = splitDuration(iso);
  if (!parsed) return iso;
  let rest =
    parsed.amount * (UNIT_SECONDS.find(([unit]) => unit === parsed.unit)?.[1] ?? 1);
  const parts: string[] = [];
  for (const [unit, seconds] of UNIT_SECONDS) {
    const count = unit === 'seconds' ? rest : Math.floor(rest / seconds);
    rest = Math.round((rest - count * seconds) * 1e6) / 1e6;
    if (count > 0) {
      parts.push(
        translate.instant(`${PATH_FILTER_I18N}.value.duration_short.${unit}`, { count })
      );
    }
  }
  return parts.length
    ? parts.join(' ')
    : translate.instant(`${PATH_FILTER_I18N}.value.duration_short.seconds`, { count: 0 });
}

/**
 * A closed field's values as select items. A selected value the field no longer serves stays
 * listed after them, so it can still be seen and dropped.
 */
export function closedValueItems(
  field: Pick<FilterField, 'enumValues' | 'enumLabels'> | undefined,
  selected: readonly string[],
  lang: AvailableLanguages
): SelectItem<string>[] {
  const served = field?.enumValues ?? [];
  const offList = selected.filter((value) => value !== '' && !served.includes(value));
  return [...served, ...offList].map((value) => ({
    id: value,
    label: closedValueLabel(field, value, lang)
  }));
}

/** A closed value by its served title in `lang`, else by the raw value the wire carries. */
export function closedValueLabel(
  field: Pick<FilterField, 'enumLabels'> | undefined,
  value: string,
  lang: AvailableLanguages
): string {
  return field?.enumLabels?.[value]?.[lang] || value;
}

function literalText(
  literal: FilterLiteral | null | undefined,
  translate: TranslateService,
  valueLabel: (value: string) => string = (value) => value
): string {
  if (literal == null) return '';
  if (literal !== 'null' && 'duration' in literal) {
    return durationText(literal.duration, translate);
  }
  if (literal !== 'null' && 'string' in literal) return valueLabel(literal.string);
  if (literal !== 'null' && 'uid' in literal) return literal.uid;
  return printLiteral(literal);
}

/**
 * A short reading of an operator argument, for a pill; empty while the argument is missing.
 * `valueLabel` names the string values a field compares against, e.g. by `closedValueLabel`.
 */
export function argumentSummary(
  op: FilterOpName,
  arg: OpArgument | null,
  translate: TranslateService,
  valueLabel?: (value: string) => string
): string {
  if (arg === null) return '';
  switch (FILTER_OP_SHAPES[op]) {
    case 'none':
      return '';
    case 'literal':
      return literalText(arg as FilterLiteral, translate, valueLabel);
    case 'range': {
      const range = arg as LiteralRange;
      return `${literalText(range.lo, translate)} – ${literalText(range.hi, translate)}`;
    }
    case 'literals':
      return (arg as FilterLiteral[])
        .map((item) => literalText(item, translate, valueLabel))
        .join(', ');
    case 'relative_date':
      return relativeDateLabel(arg as RelativeDateSpec, translate);
    case 'text':
      return arg as string;
    case 'texts':
      return (arg as string[]).join(', ');
    case 'label_literal': {
      const value = arg as LabelLiteral;
      return `${value.label} ${literalText(value.value, translate)}`;
    }
    case 'label_literals': {
      const value = arg as LabelLiterals;
      return `${value.label} ${value.values.map((item) => literalText(item, translate)).join(', ')}`;
    }
    case 'label_range': {
      const value = arg as LabelRange;
      return `${value.label} ${literalText(value.lo, translate)} – ${literalText(value.hi, translate)}`;
    }
    case 'label_text': {
      const value = arg as LabelText;
      return `${value.label} ${value.value}`;
    }
  }
}

export const FIELD_TYPE_ICONS: Record<FieldType, string> = {
  UID: ICONS.link,
  UID_LIST: ICONS.link,
  NUMBER: ICONS.hash,
  INTEGER: ICONS.hash,
  BOOL: ICONS.toggleRight,
  TEXT: ICONS.textAa,
  MASS: ICONS.scales,
  DURATION: ICONS.hourglass,
  LENGTH: ICONS.ruler,
  DATE: ICONS.calendar,
  DATE_TIME: ICONS.clock,
  TAGS: ICONS.tag,
  LABELS: ICONS.tagSimple,
  LIST: ICONS.listChecks
};

export function fieldTypeHint(type: FieldType, translate: TranslateService): string {
  return translate.instant(`${PATH_FILTER_I18N}.field_types.${type}`);
}
